/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.DASHBOARD — mobile-native.
 *
 * Touch-first counterpart of src/pages/eds/EdsDashboardPage.tsx. A DISTINCT
 * presentation (KPI tiles + scannable per-campaign cards + bottom-sheet detail)
 * over the SAME read-only data layer — never the desktop grid re-hosted (§16):
 *   - reads through the shared EDS data/logic service `listCampaignStats()`
 *     (src/services/eds/EdsSendService.ts), which itself aggregates via apiDb
 *     over eds_campaign / eds_email_log / eds_email_event through the gateway —
 *     the exact same read path the desktop dashboard uses, reused rather than
 *     re-implemented (DRY; §16 "same reads via apiDb").
 *   - campaign_name is a real display name (no FK/id ever reaches render, §17.B4);
 *     the campaign status is a CHECK-enum, so its label goes through i18n via a
 *     labelled constant, never the stored literal (§17.B11).
 * This screen is READ-ONLY: it performs no writes, so it imports neither
 * routed_writes nor any mutation path (§2 — there is simply nothing to write).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Mail } from 'lucide-react';
import { listCampaignStats, type CampaignStats } from '../../services/eds/EdsSendService';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Campaign-status → localized label + status-pill + lead tone.
// §17.B11: the stored enum literal is never rendered; the label resolves through
// i18n (same eds.status.* keys the desktop dashboard uses) with a neutral
// English fallback. Keyed lowercase; the stored value is normalized on lookup.
// ---------------------------------------------------------------------------

type PillTone = 'm-p-ok' | 'm-p-info' | 'm-p-wait' | 'm-p-no' | '';
type LeadTone = 'm-l-green' | 'm-l-blue' | 'm-l-amber' | 'm-l-red' | 'm-l-slate';

interface StatusMeta {
  labelKey: string;
  label: string;
  pill: PillTone;
  lead: LeadTone;
}

const STATUS_META: Record<string, StatusMeta> = {
  draft:                 { labelKey: 'eds.status.draft',                 label: 'Draft',                 pill: '',        lead: 'm-l-slate' },
  scheduled:             { labelKey: 'eds.status.scheduled',             label: 'Scheduled',             pill: 'm-p-info', lead: 'm-l-blue'  },
  queued:                { labelKey: 'eds.status.queued',                label: 'Queued',                pill: 'm-p-info', lead: 'm-l-blue'  },
  sending:               { labelKey: 'eds.status.sending',               label: 'Sending',               pill: 'm-p-info', lead: 'm-l-amber' },
  sent:                  { labelKey: 'eds.status.sent',                  label: 'Sent',                  pill: 'm-p-ok',   lead: 'm-l-green' },
  completed:             { labelKey: 'eds.status.completed',             label: 'Completed',             pill: 'm-p-ok',   lead: 'm-l-green' },
  completed_with_errors: { labelKey: 'eds.status.completed_with_errors', label: 'Completed with errors', pill: 'm-p-wait', lead: 'm-l-amber' },
  paused:                { labelKey: 'eds.status.paused',                label: 'Paused',                pill: 'm-p-wait', lead: 'm-l-amber' },
  cancelled:             { labelKey: 'eds.status.cancelled',             label: 'Cancelled',             pill: 'm-p-no',   lead: 'm-l-red'   },
  failed:                { labelKey: 'eds.status.failed',                label: 'Failed',                pill: 'm-p-no',   lead: 'm-l-red'   },
};

// Map the internal lead/pill class strings onto the Direction-C primitive tones.
const LEAD_TONE_KEY: Record<LeadTone, MobileLeadTone> = {
  'm-l-green': 'green', 'm-l-blue': 'blue', 'm-l-amber': 'amber', 'm-l-red': 'red', 'm-l-slate': 'slate',
};
const PILL_TONE_KEY: Record<Exclude<PillTone, ''>, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-info': 'info', 'm-p-wait': 'wait', 'm-p-no': 'no',
};

// Bounce-rate thresholds (percent) that flip the KPI tile / detail row to alert.
const BOUNCE_ALERT_PCT = 5;

// ---------------------------------------------------------------------------

export function MobileEdsDashboardPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<CampaignStats[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState('all');
  const [selected, setSelected] = useState<CampaignStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await listCampaignStats();
      setStats(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const metaOf = (status: string): StatusMeta | undefined => STATUS_META[status.toLowerCase()];
  const statusLabel = (status: string): string => {
    const meta = metaOf(status);
    return meta ? t(meta.labelKey, meta.label) : status;
  };
  // Render a status pill via the primitive when the tone is one of the five; the
  // neutral 'draft' (pill: '') keeps a bare .m-pill (no primitive neutral tone).
  const statusPill = (status: string) => {
    const cls = metaOf(status)?.pill ?? '';
    return cls
      ? <MobileStatusPill tone={PILL_TONE_KEY[cls]}>{statusLabel(status)}</MobileStatusPill>
      : <span className="m-pill">{statusLabel(status)}</span>;
  };

  // ── Filter ────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return stats.filter((s) => {
      if (statusFacet !== 'all' && s.status.toLowerCase() !== statusFacet) return false;
      if (!q) return true;
      return s.campaign_name.toLowerCase().includes(q);
    });
  }, [stats, filter, statusFacet]);

  // ── Aggregate KPI summary across the filtered set (mirrors the desktop bar) ──

  const agg = useMemo(() => {
    const totalSent      = filtered.reduce((s, r) => s + r.sent_count,      0);
    const totalDelivered = filtered.reduce((s, r) => s + r.delivered_count, 0);
    const totalBounced   = filtered.reduce((s, r) => s + r.bounced_count,   0);
    const totalOpened    = filtered.reduce((s, r) => s + r.opened_count,    0);
    const totalOptOut    = filtered.reduce((s, r) => s + r.opted_out_count, 0);
    return {
      campaigns:    filtered.length,
      sent:         totalSent,
      deliveredPct: totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0,
      bouncePct:    totalSent > 0 ? Math.round((totalBounced / totalSent) * 100) : 0,
      openRate:     totalDelivered > 0 ? Math.round((totalOpened / totalDelivered) * 100) : 0,
      optOuts:      totalOptOut,
    };
  }, [filtered]);

  // ── Status facet chips — 'all' plus every status actually present ──────────

  const facets = useMemo(() => {
    const present = Array.from(new Set(stats.map((s) => s.status.toLowerCase()))).sort();
    return ['all', ...present];
  }, [stats]);

  const facetLabel = (key: string): string =>
    key === 'all' ? t('mobile.eds_dashboard.all', 'All') : statusLabel(key);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.eds_dashboard.title', 'Email Campaigns')}
        description={t('mobile.eds_dashboard.description', 'Delivery and engagement performance across your email campaigns.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.campaigns', 'Campaigns')}</div>
          <div className="m-kpi-v">{agg.campaigns.toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.emails_sent', 'Emails Sent')}</div>
          <div className="m-kpi-v">{agg.sent.toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.delivered', 'Delivered')}</div>
          <div className="m-kpi-v">{agg.deliveredPct}%</div>
        </div>
        <div className={'m-kpi' + (agg.bouncePct > BOUNCE_ALERT_PCT ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.bounce_rate', 'Bounce Rate')}</div>
          <div className="m-kpi-v">{agg.bouncePct}%</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.open_rate', 'Open Rate')}</div>
          <div className="m-kpi-v">{agg.openRate}%</div>
        </div>
        <div className={'m-kpi' + (agg.optOuts > 0 ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.eds_dashboard.kpi.opt_outs', 'Opt-Outs')}</div>
          <div className="m-kpi-v">{agg.optOuts.toLocaleString()}</div>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.eds_dashboard.search', 'Search campaigns')}
          aria-label={t('mobile.eds_dashboard.search', 'Search campaigns')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((key) => ({ value: key, label: facetLabel(key) }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard>
          <div className="m-empty"><Inbox /> {t('mobile.eds_dashboard.none', 'No campaigns match')}</div>
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((c) => (
            <MobileCardRow
              key={c.campaign_id}
              lead={<Mail />}
              leadTone={LEAD_TONE_KEY[metaOf(c.status)?.lead ?? 'm-l-slate']}
              title={c.campaign_name}
              subtitle={
                <>
                  {t('mobile.eds_dashboard.recipients_n', { count: c.total_recipients, defaultValue: `${c.total_recipients} recipients` })}
                  {' · '}
                  {t('mobile.eds_dashboard.sent_n', { count: c.sent_count, defaultValue: `${c.sent_count} sent` })}
                </>
              }
              trailing={statusPill(c.status)}
              chevron
              onClick={() => setSelected(c)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only per-campaign breakdown) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.eds_dashboard.detail', 'Campaign detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Mail size={20} /></span>
              <span>
                <div className="m-nm">{selected.campaign_name}</div>
                <div className="m-em">{statusLabel(selected.status)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.eds_dashboard.f.status', 'Status')} value={statusPill(selected.status)} />
              <MobileDetailRow label={t('mobile.eds_dashboard.f.recipients', 'Recipients')} value={selected.total_recipients.toLocaleString()} />
              <MobileDetailRow label={t('mobile.eds_dashboard.f.sent', 'Sent')} value={selected.sent_count.toLocaleString()} />
              <MobileDetailRow label={t('mobile.eds_dashboard.f.delivered', 'Delivered')} value={`${selected.delivered_count.toLocaleString()} (${selected.delivered_pct}%)`} />
              <MobileDetailRow
                label={t('mobile.eds_dashboard.f.bounced', 'Bounced')}
                value={
                  <span className={'m-pill ' + (selected.bounce_pct > BOUNCE_ALERT_PCT ? 'm-p-no' : '')}>
                    {selected.bounced_count.toLocaleString()} ({selected.bounce_pct}%)
                  </span>
                }
              />
              <MobileDetailRow label={t('mobile.eds_dashboard.f.opened', 'Opened')} value={`${selected.opened_count.toLocaleString()} (${selected.open_rate_pct}%)`} />
              <MobileDetailRow label={t('mobile.eds_dashboard.f.clicked', 'Clicks')} value={selected.clicked_count.toLocaleString()} />
              <MobileDetailRow
                label={t('mobile.eds_dashboard.f.opt_outs', 'Opt-Outs')}
                value={
                  <span className={'m-pill ' + (selected.opted_out_count > 0 ? 'm-p-wait' : '')}>
                    {selected.opted_out_count.toLocaleString()}
                  </span>
                }
              />
              <MobileDetailRow
                label={t('mobile.eds_dashboard.f.failed', 'Failed')}
                value={
                  <span className={'m-pill ' + (selected.failed_count > 0 ? 'm-p-no' : '')}>
                    {selected.failed_count.toLocaleString()}
                  </span>
                }
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
